import { supabase } from "~/integrations/supabase/client";

import type {
  PayableDocument,
  PaySupplierInvoicesInput,
  PaySupplierInvoicesResult,
} from "~/types/purchasing";

// RPC added after the last supabase types regeneration — thin typed wrapper (same approach as
// supplierInvoiceService / supplierBalanceService).
const rpcCall = supabase.rpc.bind(supabase) as unknown as <T>(
  fn: string,
  args?: Record<string, unknown>
) => Promise<{ data: T | null; error: { message: string } | null }>;

export const supplierPaymentService = {
  /** All open payable documents (facturas + saldo inicial) across suppliers, optionally per empresa.
   * Feeds the global "Pagos a Proveedor" view. `payable_clp` is the positive amount owed. */
  async getPayableDocuments(companyId?: string): Promise<PayableDocument[]> {
    const { data, error } = await rpcCall<PayableDocument[]>(
      "get_supplier_payable_documents",
      { p_company_id: companyId ?? null }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** Pay one or more open documents in a single draft asiento (Dr Proveedores / Cr Banco) in the
   * PAGO_PROVEEDORES NACIONALES journal, and rebajar el saldo. Publish it from Movimientos. */
  async pay(input: PaySupplierInvoicesInput): Promise<PaySupplierInvoicesResult> {
    const { data, error } = await rpcCall<PaySupplierInvoicesResult>(
      "pay_supplier_invoices",
      {
        p_payment_date: input.paymentDate,
        p_bank_account_id: input.bankAccountId,
        p_items: input.items,
        p_ref: input.ref ?? null,
        p_is_admin: input.isAdmin ?? false,
      }
    );
    if (error) throw new Error(error.message);
    return data as PaySupplierInvoicesResult;
  },

  /** Devolver un borrador de pago a "Pagos a Proveedor": soft-delete del asiento borrador. Esto
   * dispara el trigger trg_sync_supplier_payment_reversal, que restaura el saldo de los documentos
   * y marca el pago como reversado, dejándolos nuevamente pendientes de pago. Solo aplica a
   * borradores (asientos no publicados). */
  async returnDraft(movementId: string): Promise<void> {
    const { error } = await rpcCall<boolean>("soft_delete_account_movement", {
      movement_id: movementId,
    });
    if (error) throw new Error(error.message);
  },
};
